import { Layer } from 'shared/map-exports';
import { EvacZoneStatus, RegionEvacZoneStyle } from 'shared/types';
import {
  EvacZonePoi,
  MapClickGetPois,
  StandardPoi,
} from 'features/mapping/mapping.types';
import { usePoisState } from 'state/usePoisState';
import { useMapLayersState } from 'state/useMapLayersState';
import { useAppInfo } from 'hooks/useAppInfo';
import { getWatchdutyTileUrl } from 'features/mapping/components/layers/layers.utils';
import {
  BELOW_STREETS_LAYER_ID,
  ON_TOP_OF_EVERYTHING_LAYER_ID,
} from 'features/mapping/styles';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import { EvacZoneStyles } from '../../../../../constants';
import {
  FEATURE_UID_V2_FIELD_NAME,
  ZONES_SOURCE_LAYER,
  EVAC_ZONES_SOURCE_LAYER_NAME,
  SOURCE_ID,
} from './constants';
import { getSelectedEvacZoneUId } from './utils';
import {
  getFillStyleAll,
  getLabelStyleAll,
  getSelectedZonePatternStyle,
  getSelectedZoneStrokeStyle,
  getStrokeStyleAll,
} from './layerStyles';

type StructuredEvacuationsViewLayerProps = {
  visible: boolean;
};

type AllEvacZonesProperties = {
  display_name: string;
  source_attribution: string;
  uid_v2: string;
  status?: EvacZoneStatus | '';
  evac_zone_style: RegionEvacZoneStyle;
  region_id: number;
  date_created: string;
  date_modified: string;
};

const REFRESH_INTERVAL_SECONDS = 3600; // 1 hour
const VIEW_SOURCE_ID = `${SOURCE_ID}_ALL`;
export const EVAC_ZONE_FILL_LAYER_ID = 'fill_layer_all';

export const evacZoneClickHandler: MapClickGetPois<AllEvacZonesProperties> = (
  geoJsonFeatures,
  activeEvacZones,
) => {
  return geoJsonFeatures
    .map((feature) => {
      if (!feature.properties) return null;

      const activeEvacZone = activeEvacZones.find(
        (activeZone) => activeZone.evacZone.uidV2 === feature.properties.uid_v2,
      );

      const poiDialogData: EvacZonePoi = {
        uid: feature.properties.uid_v2,
        displayName: feature.properties.display_name,
        status: feature.properties.status || undefined,
        dateCreated:
          activeEvacZone?.dateCreated || feature.properties.date_created,
        dateModified:
          activeEvacZone?.dateModified || feature.properties.date_modified,
        source: feature.properties.source_attribution,
        style: feature.properties.evac_zone_style || EvacZoneStyles.default,
      };

      return {
        type: 'evacZone',
        id: feature.properties.uid_v2,
        metadata: poiDialogData,
      };
    })
    .filter(Boolean) as StandardPoi[];
};

/*
 * This layer is designed to show all pre-planned zones available with no filtering directly from the tile feature
 * properties with no affiliation to our backend evac zone data and/or statuses.
 */
export const StructuredEvacuationsAllLayer = (
  props: StructuredEvacuationsViewLayerProps,
): JSX.Element => {
  const { visible } = props;
  const { selectedPoi } = usePoisState();
  const { isDarkBaseLayer } = useMapLayersState();
  const { appVersion } = useAppInfo();

  const selectedEvacZoneUId = getSelectedEvacZoneUId(selectedPoi);

  return (
    <VisibilityToggledSource
      url={getWatchdutyTileUrl(EVAC_ZONES_SOURCE_LAYER_NAME, {
        app_version: appVersion,
      })}
      visible={visible}
      promoteId={FEATURE_UID_V2_FIELD_NAME}
      // Do not unset `volatile` ever for this layer.
      volatile
      id={VIEW_SOURCE_ID}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
    >
      <Layer
        id={EVAC_ZONE_FILL_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getFillStyleAll()}
      />
      <Layer
        id="stroke_layer_all"
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getStrokeStyleAll(isDarkBaseLayer)}
      />

      <Layer
        id="selected_pattern_all"
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getSelectedZonePatternStyle(
          selectedEvacZoneUId,
          ZONES_SOURCE_LAYER,
          FEATURE_UID_V2_FIELD_NAME,
        )}
      />
      <Layer
        id="selected_stroke_all"
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getSelectedZoneStrokeStyle(
          selectedEvacZoneUId,
          ZONES_SOURCE_LAYER,
          FEATURE_UID_V2_FIELD_NAME,
        )}
      />
      <Layer
        id="zones_label_all"
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getLabelStyleAll()}
      />
    </VisibilityToggledSource>
  );
};
